import React from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import ImageAndTextRow from '@components/shared/ImageAndTextRow';
import Text from '@components/shared/Text';
import TwoColumn from '@components/shared/TwoColumn';
import { PageSlice, SliceTextFeatureDetails, SliceTextFeatureItem } from '@contracts';

interface Props {
  slice: PageSlice;
}

const TextFeatureRow: React.FC<Props> = ({ slice }) => {
  const primary = slice.primary as SliceTextFeatureDetails;
  const items = slice.items as SliceTextFeatureItem[];
  const textColor = primary.text_color;
  const textHTML = primary?.text.html;
  const featureHeadingText = primary?.feature_heading.html;
  const gatsbyImage = primary.image.gatsbyImageData;
  const image = getImage(primary.image);
  return (
    <Container $textColor={textColor}>
      <TwoColumn alignment="top" breakpoint="1024px">
        {gatsbyImage ? (
          <ImageWrap>
            <GatsbyImage image={image} alt={primary.image?.alt || ''} objectFit="contain" />
          </ImageWrap>
        ) : (
          <TextContainer>
            <Text text={textHTML} />
          </TextContainer>
        )}
        <ImgTextContainer>
          <FeaturesHeadingText dangerouslySetInnerHTML={{ __html: featureHeadingText }} />
          {items?.map((row, index) => (
            <ImageAndTextRow data={row} key={index} />
          ))}
        </ImgTextContainer>
      </TwoColumn>
    </Container>
  );
};

export default TextFeatureRow;

interface StyleProps {
  $textColor?: string;
}

const Container = styled.div<StyleProps>`
  color: ${(props) => props.$textColor};
  display: flex;
  justify-content: center;
  margin-top: 100px;

  @media (max-width: 1024px) {
    margin-bottom: 50px;
    margin-top: 90px;
  }
`;

const TextContainer = styled.div`
  margin-right: 159px;
  align-items: flex-start;
  max-width: 358px;

  h4 {
    margin-bottom: 20px;

    font-family: Domaine;
    font-size: 30px;
    font-weight: 500;
    line-height: 39px;
    text-align: left;
  }

  p {
    font-family: BrownStd;
    font-size: 18px;
    font-weight: 400;
    line-height: 27px;
    text-align: left;
  }

  @media (max-width: 1024px) {
    max-width: 600px;
    margin-right: 0;
    margin-bottom: 30px;

    h4 {
      margin-inline: 30px;

      font-size: 24px;
      line-height: 29px;
      letter-spacing: -0.02em;
      text-align: center;
    }

    p {
      margin: 0 45px;

      font-size: 16px;
      line-height: 24px;
      text-align: center;
    }
  }
`;

const ImgTextContainer = styled.div`
  max-width: 600px;

  @media (max-width: 1024px) {
    width: 100%;
    flex-direction: column;
    align-self: center;
    gap: 30px;
    margin-top: 20px;
  }
`;

const FeaturesHeadingText = styled.div`
  border-bottom: 1px solid #faead6;
  padding-bottom: 20px;
  text-align: left;
  max-width: 400px;
  align-self: center;

  @media (max-width: 1024px) {
    padding: 0 30px 20px;
    text-align: center;
  }

  h3 {
    font-family: 'Domaine';
    font-style: normal;
    font-weight: 600;
    font-size: 30px;
    line-height: 120%;
    color: inherit;
    margin-bottom: 20px;
  }

  h5,
  h6 {
    font-family: BrownStd;
    font-style: normal;
    font-weight: bold;
    font-size: 13px;
    line-height: 26px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: inherit;
  }
`;

const ImageWrap = styled.div`
  width: 50%;
  height: 70%;
  margin: 0;

  @media (max-width: 1024px) {
    margin: auto;
  }
`;
